import { useEffect } from 'react';

// Only runs on native (iOS/Android) — safely no-ops in the browser
const isNative = () => {
  try {
    return !!(window.Capacitor?.isNativePlatform?.());
  } catch {
    return false;
  }
};

const API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';

export function usePushNotifications(userId) {
  useEffect(() => {
    if (!isNative()) return;

    let cleanup = null;

    (async () => {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');

        // Request permission
        const permission = await PushNotifications.requestPermissions();
        if (permission.receive !== 'granted') return;

        // Register with APNs / FCM
        await PushNotifications.register();

        // Send device token to server so we can push to this device
        const tokenListener = await PushNotifications.addListener('registration', async (token) => {
          if (!userId) return;
          try {
            const authToken = localStorage.getItem('mca_token');
            await fetch(`${API}/notify/device-token`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
              },
              body: JSON.stringify({ userId, token: token.value, platform: window.Capacitor?.getPlatform?.() }),
            });
          } catch { /* non-fatal — app works fine without push */ }
        });

        // Show notification when app is in foreground
        const foregroundListener = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('[Push] Foreground notification:', notification.title);
        });

        // Handle tap on notification (app in background/closed)
        const actionListener = await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
          const data = action.notification.data;
          if (data?.path) {
            window.location.href = data.path;
          } else if (data?.clientId) {
            window.location.href = `/clients/${data.clientId}`;
          } else if (data?.screen === 'submissions') {
            window.location.href = '/submissions';
          }
        });

        cleanup = () => {
          tokenListener.remove();
          foregroundListener.remove();
          actionListener.remove();
        };
      } catch {
        // Capacitor not available (browser) — silent no-op
      }
    })();

    return () => { if (cleanup) cleanup(); };
  }, [userId]);
}
